import React, { useState, useMemo } from 'react';
import {
  DataTable,
  DataTableHeader,
  DataTableHeadCell,
  DataTableBody,
  PageToolbar,
  Breadcrumb,
  PeriodPicker,
  StatusPill,
  Tabs,
} from '@/shared/components/ui';
import type { PeriodPickerValue, TabItem } from '@/shared/components/ui';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { WidgetHeaderIcon } from '@/shared/components/ui/WidgetHeaderIcon';
import { useCRMDashboard, useLeadConversion, useFunnelAnalytics, useTeamPerformance, useActivityReport } from '../hooks';
import {
  DashboardSummaryCard,
  FunnelChart,
  LeadConversionChart,
  TeamPerformanceTable,
  ActivitySummaryCard,
  TopLostReasonsChart,
} from '../components';
import { Icon } from '@/shared/icons'
type ReportTab = 'dashboard' | 'funnel' | 'conversion' | 'team' | 'activities';

/** Dias de janela rolling por modo do PeriodPicker (Semana/Mês/Ano). */
const PERIOD_MODE_DAYS: Record<'week' | 'month' | 'year', number> = {
  week: 7,
  month: 30,
  year: 365,
};

/**
 * Página principal de relatórios CRM
 */
export const CRMReportsPage: React.FC = () => {
  // State para filtros
  const [activeTab, setActiveTab] = useState<ReportTab>('dashboard');
  // Período via shared PeriodPicker (F81): Semana/Mês/Ano rolling + Personalizado
  const [periodValue, setPeriodValue] = useState<PeriodPickerValue>({ mode: 'month' });

  const dateRange = useMemo(() => {
    if (periodValue.mode === 'custom' && periodValue.start && periodValue.end) {
      return { startDate: periodValue.start, endDate: periodValue.end };
    }
    const days =
      periodValue.mode === 'custom'
        ? PERIOD_MODE_DAYS.month
        : PERIOD_MODE_DAYS[periodValue.mode];
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
  }, [periodValue]);

  // Hooks para dados
  const { data: dashboardData, isLoading: dashboardLoading } = useCRMDashboard(dateRange);
  const { data: conversionData, isLoading: conversionLoading } = useLeadConversion(dateRange);
  const { data: funnelData, isLoading: funnelLoading } = useFunnelAnalytics(dateRange);
  const { data: teamData, isLoading: teamLoading } = useTeamPerformance(dateRange);
  const { data: activityData, isLoading: activityLoading } = useActivityReport(dateRange);

  // Render Dashboard Tab
  const renderDashboard = () => {
    if (dashboardLoading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-32 rounded-lg animate-pulse"
              style={{ backgroundColor: 'var(--theme-card-background)' }}
            />
          ))}
        </div>
      );
    }

    if (!dashboardData) {
      return (
        <div
          className="rounded-lg p-8 text-center"
          style={{ backgroundColor: 'var(--theme-card-background)' }}
        >
          <Icon name="alert-circle" size={48} className="mx-auto mb-4 opacity-30" />
          <p style={{ color: 'var(--theme-muted-text)' }}>
            Não há dados disponíveis para o período selecionado
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <DashboardSummaryCard
            title="Total de Leads"
            value={dashboardData.leads.total}
            icon={'users'}
            trend={dashboardData.leads.trend}
          />
          <DashboardSummaryCard
            title="Taxa de Conversão"
            value={`${dashboardData.leads.conversionRate.toFixed(1)}%`}
            icon={'trending-up'}
            subtitle={`${dashboardData.leads.won} leads ganhos`}
          />
          <DashboardSummaryCard
            title="Valor Total"
            value={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(dashboardData.leads.totalValue)}
            icon={'dollar-sign'}
            subtitle={`${dashboardData.leads.inProgress} em andamento`}
          />
          <DashboardSummaryCard
            title="Atividades"
            value={dashboardData.activities.total}
            icon={'activity'}
            subtitle={`${dashboardData.activities.overdue} atrasadas`}
          />
        </div>

        {/* Second Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Leads by status */}
          <div
            className="rounded-lg border flex flex-col"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              borderColor: 'var(--theme-card-border)',
            }}
          >
            <div className="px-4 py-3 pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <h3
                    className="text-[15px] font-bold tracking-[-0.01em]"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    Status dos Leads
                  </h3>
                  <p
                    className="text-xs mt-0.5"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    Distribuição por status
                  </p>
                </div>
                <WidgetHeaderIcon icon={'target'} />
              </div>
            </div>
            <div className="flex-1 px-4 pb-4">
              <div className="flex items-center justify-between border-t first:border-t-0" style={{ padding: '9px 0', borderColor: 'var(--theme-card-border)' }}>
                <span className="text-[13px]" style={{ color: 'var(--theme-muted-text)' }}>Novos</span>
                <span
                  className="text-[13px] font-bold"
                  style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
                >
                  {dashboardData.leads.new}
                </span>
              </div>
              <div className="flex items-center justify-between border-t first:border-t-0" style={{ padding: '9px 0', borderColor: 'var(--theme-card-border)' }}>
                <span className="text-[13px]" style={{ color: 'var(--theme-muted-text)' }}>Em andamento</span>
                <span
                  className="text-[13px] font-bold"
                  style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
                >
                  {dashboardData.leads.inProgress}
                </span>
              </div>
              <div className="flex items-center justify-between border-t first:border-t-0" style={{ padding: '9px 0', borderColor: 'var(--theme-card-border)' }}>
                <span className="text-[13px]" style={{ color: 'var(--theme-success-color)' }}>Ganhos</span>
                <span
                  className="text-[13px] font-bold"
                  style={{ color: 'var(--theme-success-color)', fontVariantNumeric: 'tabular-nums' }}
                >
                  {dashboardData.leads.won}
                </span>
              </div>
              <div className="flex items-center justify-between border-t first:border-t-0" style={{ padding: '9px 0', borderColor: 'var(--theme-card-border)' }}>
                <span className="text-[13px]" style={{ color: 'var(--theme-error-color)' }}>Perdidos</span>
                <span
                  className="text-[13px] font-bold"
                  style={{ color: 'var(--theme-error-color)', fontVariantNumeric: 'tabular-nums' }}
                >
                  {dashboardData.leads.lost}
                </span>
              </div>
            </div>
          </div>

          {/* Activity Summary */}
          <div
            className="rounded-lg border flex flex-col"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              borderColor: 'var(--theme-card-border)',
            }}
          >
            <div className="px-4 py-3 pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <h3
                    className="text-[15px] font-bold tracking-[-0.01em]"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    Atividades
                  </h3>
                  <p
                    className="text-xs mt-0.5"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    Resumo de atividades
                  </p>
                </div>
                <WidgetHeaderIcon icon={'activity'} />
              </div>
            </div>
            <div className="flex-1 px-4 pb-4">
              <div className="flex items-center justify-between border-t first:border-t-0" style={{ padding: '9px 0', borderColor: 'var(--theme-card-border)' }}>
                <span className="text-[13px]" style={{ color: 'var(--theme-muted-text)' }}>Concluídas</span>
                <div className="flex items-center gap-2">
                  <Icon name="check-circle" className="h-3.5 w-3.5"
                    style={{ color: 'var(--theme-success-color)' }} />
                  <span
                    className="text-[13px] font-bold"
                    style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {dashboardData.activities.completed}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between border-t first:border-t-0" style={{ padding: '9px 0', borderColor: 'var(--theme-card-border)' }}>
                <span className="text-[13px]" style={{ color: 'var(--theme-muted-text)' }}>Pendentes</span>
                <div className="flex items-center gap-2">
                  <Icon name="clock" className="h-3.5 w-3.5"
                    style={{ color: 'var(--theme-warning-color)' }} />
                  <span
                    className="text-[13px] font-bold"
                    style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {dashboardData.activities.pending}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between border-t first:border-t-0" style={{ padding: '9px 0', borderColor: 'var(--theme-card-border)' }}>
                <span className="text-[13px]" style={{ color: 'var(--theme-muted-text)' }}>Atrasadas</span>
                <div className="flex items-center gap-2">
                  <Icon name="alert-circle" className="h-3.5 w-3.5"
                    style={{ color: 'var(--theme-error-color)' }} />
                  <span
                    className="text-[13px] font-bold"
                    style={{ color: 'var(--theme-error-color)', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {dashboardData.activities.overdue}
                  </span>
                </div>
              </div>
              <div
                className="pt-2 border-t"
                style={{ borderColor: 'var(--theme-card-border)' }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[13px]" style={{ color: 'var(--theme-muted-text)' }}>Taxa de conclusão</span>
                  <span
                    className="text-[13px] font-bold"
                    style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {dashboardData.activities.completionRate.toFixed(0)}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Funnels summary */}
          <div
            className="rounded-lg border flex flex-col"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              borderColor: 'var(--theme-card-border)',
            }}
          >
            <div className="px-4 py-3 pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <h3
                    className="text-[15px] font-bold tracking-[-0.01em]"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    Funis
                  </h3>
                  <p
                    className="text-xs mt-0.5"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    Taxas de conversão por funil
                  </p>
                </div>
                <WidgetHeaderIcon icon={'layers'} />
              </div>
            </div>
            <div className="flex-1 px-4 pb-4">
              {dashboardData.funnels && dashboardData.funnels.length > 0 ? (
                <div>
                  {dashboardData.funnels.slice(0, 4).map((funnel) => (
                    <div key={funnel.id} className="flex items-center justify-between border-t first:border-t-0" style={{ padding: '9px 0', borderColor: 'var(--theme-card-border)' }}>
                      <span
                        className="text-[13px] truncate flex-1"
                        style={{ color: 'var(--theme-card-text)' }}
                      >
                        {funnel.name}
                      </span>
                      <div className="flex items-center gap-3">
                        <span
                          className="text-[13px]"
                          style={{ color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}
                        >
                          {funnel.leadsCount} leads
                        </span>
                        <span
                          className="text-[13px] font-bold"
                          style={{ color: 'var(--theme-success-color)', fontVariantNumeric: 'tabular-nums' }}
                        >
                          {funnel.conversionRate.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p
                  className="text-xs text-center py-4"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  Nenhum funil encontrado
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Recent Leads */}
        {dashboardData.recentLeads && dashboardData.recentLeads.length > 0 && (
          <div
            className="rounded-lg border"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              borderColor: 'var(--theme-card-border)',
            }}
          >
            <div className="px-4 py-3 pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <h3
                    className="text-[15px] font-bold tracking-[-0.01em]"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    Leads Recentes
                  </h3>
                  <p
                    className="text-xs mt-0.5"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    Últimos leads adicionados
                  </p>
                </div>
                <WidgetHeaderIcon icon={'users'} />
              </div>
            </div>
            <DataTable style={{ border: 'none', borderRadius: 0 }}>
              <DataTableHeader>
                <DataTableHeadCell>Contato</DataTableHeadCell>
                <DataTableHeadCell>Segmento</DataTableHeadCell>
                <DataTableHeadCell>Status</DataTableHeadCell>
                <DataTableHeadCell align="right">Valor</DataTableHeadCell>
                <DataTableHeadCell align="right">Data</DataTableHeadCell>
              </DataTableHeader>
              <DataTableBody>
                {dashboardData.recentLeads.map((lead) => (
                  <tr key={lead.id} className="border-t" style={{ borderColor: 'var(--theme-card-border)' }}>
                    <td className="px-4 py-3 text-xs font-medium" style={{ color: 'var(--theme-card-text)' }}>
                      {lead.contactName}
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                      {lead.segment}
                    </td>
                    <td className="px-4 py-3">
                      {/* StatusPill shared (F62): verde=ganho, vermelho=perdido, azul=em andamento */}
                      <StatusPill
                        variant={
                          lead.businessStatus === 'won' ? 'success' :
                          lead.businessStatus === 'lost' ? 'error' :
                          'info'
                        }
                        label={
                          lead.businessStatus === 'won' ? 'Ganho' :
                          lead.businessStatus === 'lost' ? 'Perdido' :
                          'Em andamento'
                        }
                        data-testid={`crm-reports-lead-status-${lead.id}`}
                      />
                    </td>
                    <td
                      className="px-4 py-3 text-xs text-right"
                      style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {lead.value
                        ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(lead.value)
                        : '-'}
                    </td>
                    <td
                      className="px-4 py-3 text-xs text-right"
                      style={{ color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {new Date(lead.createdAt).toLocaleDateString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </DataTableBody>
            </DataTable>
          </div>
        )}
      </div>
    );
  };

  // Render Funnel Tab
  const renderFunnel = () => {
    if (funnelLoading) {
      return (
        <div className="space-y-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="h-64 rounded-lg animate-pulse"
              style={{ backgroundColor: 'var(--theme-card-background)' }}
            />
          ))}
        </div>
      );
    }

    if (!funnelData || funnelData.length === 0) {
      return (
        <div
          className="rounded-lg p-8 text-center"
          style={{ backgroundColor: 'var(--theme-card-background)' }}
        >
          <Icon name="target" size={48} className="mx-auto mb-4 opacity-30" />
          <p style={{ color: 'var(--theme-muted-text)' }}>
            Nenhum funil encontrado para o período selecionado
          </p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {funnelData.map((funnel) => (
          <FunnelChart key={funnel.funnelId} data={funnel} />
        ))}
      </div>
    );
  };

  // Render Conversion Tab
  const renderConversion = () => {
    if (conversionLoading) {
      return (
        <div className="space-y-4">
          <div
            className="h-96 rounded-lg animate-pulse"
            style={{ backgroundColor: 'var(--theme-card-background)' }}
          />
        </div>
      );
    }

    if (!conversionData) {
      return (
        <div
          className="rounded-lg p-8 text-center"
          style={{ backgroundColor: 'var(--theme-card-background)' }}
        >
          <Icon name="trending-up" size={48} className="mx-auto mb-4 opacity-30" />
          <p style={{ color: 'var(--theme-muted-text)' }}>
            Não há dados de conversão para o período selecionado
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <DashboardSummaryCard
            title="Total de Leads"
            value={conversionData.summary.totalLeads}
            icon={'users'}

          />
          <DashboardSummaryCard
            title="Taxa de Conversão"
            value={`${conversionData.summary.conversionRate.toFixed(1)}%`}
            icon={'trending-up'}

          />
          <DashboardSummaryCard
            title="Valor Médio"
            value={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(conversionData.summary.avgValue)}
            icon={'dollar-sign'}

          />
          <DashboardSummaryCard
            title="Taxa de Perda"
            value={`${conversionData.summary.lossRate.toFixed(1)}%`}
            icon={'alert-circle'}

          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <LeadConversionChart data={conversionData.byPeriod} title="Conversão por Período" />
          <TopLostReasonsChart data={conversionData.topLostReasons} />
        </div>

        {/* By Source and Origin */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* By Source */}
          <div
            className="rounded-lg border"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              borderColor: 'var(--theme-card-border)',
            }}
          >
            <div className="px-4 py-3 pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <h3
                    className="text-[15px] font-bold tracking-[-0.01em]"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    Por Origem
                  </h3>
                  <p
                    className="text-xs mt-0.5"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    Conversão por fonte de lead
                  </p>
                </div>
                <WidgetHeaderIcon icon={'target'} />
              </div>
            </div>
            <div className="px-4 pb-4">
              {conversionData.bySource.length > 0 ? (
                <div>
                  {conversionData.bySource.map((item) => (
                    <div key={item.source} className="flex items-center justify-between border-t first:border-t-0" style={{ padding: '9px 0', borderColor: 'var(--theme-card-border)' }}>
                      <span className="text-[13px]" style={{ color: 'var(--theme-card-text)' }}>
                        {item.source}
                      </span>
                      <div className="flex items-center gap-3">
                        <span
                          className="text-[13px]"
                          style={{ color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}
                        >
                          {item.count} leads
                        </span>
                        <span
                          className="text-[13px] font-bold"
                          style={{ color: 'var(--theme-success-color)', fontVariantNumeric: 'tabular-nums' }}
                        >
                          {item.conversionRate.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-center py-4" style={{ color: 'var(--theme-muted-text)' }}>
                  Nenhum dado disponível
                </p>
              )}
            </div>
          </div>

          {/* By Origin */}
          <div
            className="rounded-lg border"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              borderColor: 'var(--theme-card-border)',
            }}
          >
            <div className="px-4 py-3 pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <h3
                    className="text-[15px] font-bold tracking-[-0.01em]"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    Por Canal
                  </h3>
                  <p
                    className="text-xs mt-0.5"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    Conversão por canal de entrada
                  </p>
                </div>
                <WidgetHeaderIcon icon={'layers'} />
              </div>
            </div>
            <div className="px-4 pb-4">
              {conversionData.byChannel.length > 0 ? (
                <div>
                  {conversionData.byChannel.map((item) => (
                    <div key={item.channel} className="flex items-center justify-between border-t first:border-t-0" style={{ padding: '9px 0', borderColor: 'var(--theme-card-border)' }}>
                      <span className="text-[13px]" style={{ color: 'var(--theme-card-text)' }}>
                        {item.channel}
                      </span>
                      <div className="flex items-center gap-3">
                        <span
                          className="text-[13px]"
                          style={{ color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}
                        >
                          {item.count} leads
                        </span>
                        <span
                          className="text-[13px] font-bold"
                          style={{ color: 'var(--theme-success-color)', fontVariantNumeric: 'tabular-nums' }}
                        >
                          {item.conversionRate.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-center py-4" style={{ color: 'var(--theme-muted-text)' }}>
                  Nenhum dado disponível
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Team Tab
  const renderTeam = () => {
    if (teamLoading) {
      return (
        <div
          className="h-96 rounded-lg animate-pulse"
          style={{ backgroundColor: 'var(--theme-card-background)' }}
        />
      );
    }

    if (!teamData) {
      return (
        <div
          className="rounded-lg p-8 text-center"
          style={{ backgroundColor: 'var(--theme-card-background)' }}
        >
          <Icon name="users" size={48} className="mx-auto mb-4 opacity-30" />
          <p style={{ color: 'var(--theme-muted-text)' }}>
            Não há dados de equipe para o período selecionado
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <DashboardSummaryCard
            title="Membros"
            value={teamData.summary.totalMembers}
            icon={'users'}

          />
          <DashboardSummaryCard
            title="Total de Leads"
            value={teamData.summary.totalLeads}
            icon={'target'}

          />
          <DashboardSummaryCard
            title="Taxa de Conversão Média"
            value={`${teamData.summary.avgConversionRate.toFixed(1)}%`}
            icon={'trending-up'}

          />
          <DashboardSummaryCard
            title="Ciclo Médio"
            value={`${teamData.summary.avgCycleTimeDays.toFixed(0)} dias`}
            icon={'clock'}

          />
        </div>

        <TeamPerformanceTable data={teamData} />
      </div>
    );
  };

  // Render Activities Tab
  const renderActivities = () => {
    if (activityLoading) {
      return (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-32 rounded-lg animate-pulse"
              style={{ backgroundColor: 'var(--theme-card-background)' }}
            />
          ))}
        </div>
      );
    }

    if (!activityData) {
      return (
        <div
          className="rounded-lg p-8 text-center"
          style={{ backgroundColor: 'var(--theme-card-background)' }}
        >
          <Icon name="activity" size={48} className="mx-auto mb-4 opacity-30" />
          <p style={{ color: 'var(--theme-muted-text)' }}>
            Não há dados de atividades para o período selecionado
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <DashboardSummaryCard
            title="Total de Atividades"
            value={activityData.summary.totalActivities}
            icon={'activity'}

          />
          <DashboardSummaryCard
            title="Concluídas"
            value={activityData.summary.completed}
            icon={'check-circle'}
            subtitle={`${activityData.summary.completionRate.toFixed(0)}% do total`}

          />
          <DashboardSummaryCard
            title="Pendentes"
            value={activityData.summary.pending}
            icon={'clock'}

          />
          <DashboardSummaryCard
            title="Atrasadas"
            value={activityData.summary.overdue}
            icon={'alert-circle'}
            subtitle={`${activityData.summary.overdueRate.toFixed(0)}% do total`}

          />
        </div>

        <ActivitySummaryCard data={activityData} />
      </div>
    );
  };

  // Conteúdo de cada aba, capado (o cap de largura é do CONTEÚDO, não do toolbar)
  const wrapTab = (content: React.ReactNode): React.ReactNode => (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">{content}</div>
  );

  // Abas via shared <Tabs pageLevel> (F55)
  const tabItems: TabItem[] = [
    { id: 'dashboard', label: 'Dashboard', icon: 'layers', content: wrapTab(renderDashboard()), 'data-testid': 'crm-reports-tab-dashboard' },
    { id: 'funnel', label: 'Funis', icon: 'target', content: wrapTab(renderFunnel()), 'data-testid': 'crm-reports-tab-funnel' },
    { id: 'conversion', label: 'Conversão', icon: 'trending-up', content: wrapTab(renderConversion()), 'data-testid': 'crm-reports-tab-conversion' },
    { id: 'team', label: 'Equipe', icon: 'users', content: wrapTab(renderTeam()), 'data-testid': 'crm-reports-tab-team' },
    { id: 'activities', label: 'Atividades', icon: 'activity', content: wrapTab(renderActivities()), 'data-testid': 'crm-reports-tab-activities' },
  ];

  // Mobile: período no ⋮ (appbar própria via PageToolbar.mobile)
  const mobilePeriodActions: CardAction[] = [
    { label: 'Semana', icon: 'calendar', onClick: () => setPeriodValue({ mode: 'week' }) },
    { label: 'Mês', icon: 'calendar', onClick: () => setPeriodValue({ mode: 'month' }) },
    { label: 'Ano', icon: 'calendar', onClick: () => setPeriodValue({ mode: 'year' }) },
  ];

  return (
    <div
      className="h-full flex flex-col"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
      data-testid="crm-reports-page"
    >
      {/* Toolbar única do DS (F23 — PageToolbar 56px, token header) */}
      <PageToolbar
        data-testid="crm-reports-toolbar"
        left={
          <Breadcrumb
            items={[
              { label: 'Relatórios' },
              { label: 'CRM', testid: 'crm-reports-breadcrumb-current' },
            ]}
            data-testid="crm-reports-breadcrumb"
          />
        }
        right={
          <div
            className="flex items-center gap-2 flex-nowrap overflow-x-auto lg:flex-wrap lg:overflow-visible"
            data-testid="crm-reports-period-selector"
          >
            <Icon name="calendar" size={16} className="flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />
            {/* Período — controle direto, shared PeriodPicker (F81) */}
            <PeriodPicker
              value={periodValue}
              onChange={setPeriodValue}
              data-testid="crm-reports-period"
            />
          </div>
        }
        mobile={
          /* Appbar mobile própria (mockup .m-appbar): título + subtítulo + período no ⋮ */
          <>
            <div className="min-w-0 flex-1">
              <div className="truncate" style={{ fontSize: 17, fontWeight: 700, color: 'var(--theme-card-text)', lineHeight: 1.15 }}>Relatório CRM</div>
              <div className="truncate" style={{ fontSize: 11, color: 'var(--theme-muted-text)', lineHeight: 1 }}>Relatórios</div>
            </div>
            <CardActionsMenu
              actions={mobilePeriodActions}
              size="sm"
              data-testid="crm-reports-mobile-period"
            />
          </>
        }
      />

      {/* Abas shared <Tabs pageLevel> (F55) + conteúdo (scroll interno do Tabs) */}
      <Tabs
        tabs={tabItems}
        activeTab={activeTab}
        onChange={(tabId) => setActiveTab(tabId as ReportTab)}
        variant="underline"
        pageLevel
        hideIconsOnMobile
      />
    </div>
  );
};

CRMReportsPage.displayName = 'CRMReportsPage';
